// Unknown patients and merging their record into the identified patient's
// (docs/plans/phase2-casualty.md §2.2, §4; ADR 0024).
//
// A casualty registers a patient before anyone knows who they are. The record is an
// ordinary patient marked unknown, with a temporary label. Once the person is identified,
// the unknown record is merged into theirs by a link: nothing is moved or rewritten, and
// the links are one level deep (an unknown record into an identified one).

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const PATIENT_IDENTITIES = ['identified', 'unknown'] as const;
export type PatientIdentity = (typeof PATIENT_IDENTITIES)[number];

/** The given name every unknown patient carries; the family name is the temporary label. */
export const UNKNOWN_GIVEN_NAME = 'Unknown';

/**
 * The name an unknown patient is registered under: "Unknown" and the temporary label, so
 * two unknown patients never look alike on a list, a label or a print.
 */
export function unknownPatientName(temporaryLabel: string): { given: string; family: string } {
  const label = temporaryLabel.trim();
  if (label === '') throw new RangeError('A temporary label cannot be blank');
  return { given: UNKNOWN_GIVEN_NAME, family: label };
}

export interface MergeCandidate {
  readonly id: string;
  readonly status: 'active' | 'entered_in_error' | 'merged';
  readonly identity: PatientIdentity;
}

export type MergeEligibilityErrorCode =
  | 'merge.source_not_unknown'
  | 'merge.source_not_active'
  | 'merge.target_invalid'
  | 'merge.same_record';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const MERGE_ELIGIBILITY_ERRORS: Record<MergeEligibilityErrorCode, string> = {
  'merge.source_not_unknown': 'Only an unidentified patient can be merged into another record',
  'merge.source_not_active': 'This record was already merged or entered in error',
  'merge.target_invalid': 'Merge into an active, identified patient',
  'merge.same_record': 'A record cannot be merged into itself',
};

/**
 * Whether the unknown `source` may be merged into `target`: the source is an active unknown
 * record, the target an active identified one, and they differ. This keeps merges one level
 * deep, so a merged record never becomes a target.
 */
export function mergeEligibility(
  source: MergeCandidate,
  target: MergeCandidate,
): Result<undefined, MergeEligibilityErrorCode> {
  if (source.id === target.id) return err('merge.same_record');
  if (source.identity !== 'unknown') return err('merge.source_not_unknown');
  if (source.status !== 'active') return err('merge.source_not_active');
  if (target.identity !== 'identified' || target.status !== 'active') {
    return err('merge.target_invalid');
  }
  return ok(undefined);
}

export type MergeConflict = 'open_visit' | 'admission';

export interface MergeSide {
  /** Types of the record's open visits at the facility (e.g. emergency, inpatient). */
  readonly openVisitTypes: readonly string[];
  /** The record has an admitted inpatient stay. */
  readonly admitted: boolean;
}

/**
 * What stops a merge being approved: both records with an open visit of the same type (one
 * person can't be in two casualty visits at once), or both admitted. Someone closes or
 * cancels one first. Returns the conflicts in a fixed order; empty means none.
 */
export function mergeConflicts(source: MergeSide, target: MergeSide): MergeConflict[] {
  const conflicts: MergeConflict[] = [];
  if (source.openVisitTypes.some((type) => target.openVisitTypes.includes(type))) {
    conflicts.push('open_visit');
  }
  if (source.admitted && target.admitted) conflicts.push('admission');
  return conflicts;
}
